<template>
  <div v-if="proList.length" class="purchase-fuel-list" role="radiogroup" :aria-label="t('enterprise_ui_purchase_choose_fuel')">
    <button
      v-for="(item, index) in proList"
      :key="item.choiceId || index"
      type="button"
      class="purchase-fuel-option"
      :class="{ 'is-selected': gearRadioValue === index }"
      role="radio"
      :aria-checked="gearRadioValue === index"
      @click="selectFuel(item, index)"
    >
      <EnterpriseIcon
        :src="gearRadioValue === index ? circleDotIcon : circleIcon"
        class="purchase-fuel-option__radio"
      />
      <span class="purchase-fuel-option__body">
        <span class="purchase-fuel-option__info">
          <strong>{{ formatQuota(item.charNum) }}</strong>
          <small>{{ formatUnitPrice(item) }}</small>
        </span>
        <span class="purchase-fuel-option__price">
          <em v-if="formatDiscount(item.discount)">{{ formatDiscount(item.discount) }}</em>
          <b>{{ formatPrice(item) }}</b>
        </span>
      </span>
    </button>
  </div>
  <div v-else class="purchase-fuel-empty">{{ t('enterprise_ui_purchase_no_fuel_currency') }}</div>
</template>

<script setup lang="ts">
import EnterpriseIcon from '~/components/Index/enterprise/EnterpriseIcon.vue'
import circleDotIcon from '~/assets/images/enterprise/icons/circle-dot.svg'
import circleIcon from '~/assets/images/enterprise/icons/circle.svg'
import { getProBusinessFuelBagConfig } from '~/config/proConfig'
import type { FuelBagConfigItem } from '~/types/pro'
import type { EnterprisePricingMode } from '~/config/enterpriseLanding'
import useProStore from '~/store/modules/pro'
import useUserStore from '~/store/modules/user'

interface Props {
  coin?: string
  pricingMode?: EnterprisePricingMode
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'selection-change': [item?: FuelBagConfigItem]
}>()
const userStore = useUserStore()
const proStore = useProStore()
const { t, locale } = useI18n()
const numberLocale = computed(() => ({ zh: 'zh-CN', tw: 'zh-TW', 'zh-tw': 'zh-TW', itb: 'it' } as Record<string, string>)[locale.value.toLowerCase()] || locale.value)
const proList = ref<FuelBagConfigItem[]>([])
const gearRadioValue = ref(-1)
const pendingChoiceId = ref('')

function selectFuel(item: FuelBagConfigItem, index: number) {
  gearRadioValue.value = index
  emit('selection-change', item)
}

function resetSelection() {
  const matchedIndex = pendingChoiceId.value
    ? proList.value.findIndex(item => item.choiceId === pendingChoiceId.value)
    : -1
  gearRadioValue.value = matchedIndex >= 0 ? matchedIndex : (proList.value.length ? 0 : -1)
  emit('selection-change', gearRadioValue.value >= 0 ? proList.value[gearRadioValue.value] : undefined)
}

function getProList() {
  proList.value = getProBusinessFuelBagConfig(props.coin?.toLowerCase(), props.pricingMode)
  resetSelection()
}

function setDefaultItem(item: FuelBagConfigItem) {
  pendingChoiceId.value = item?.choiceId || ''
  resetSelection()
}

function getPayConfigItem() {
  return gearRadioValue.value >= 0 ? proList.value[gearRadioValue.value] : undefined
}

function formatQuota(value?: number | string) {
  const amount = Number(value || 0)
  if (!Number.isFinite(amount) || amount <= 0) return t('enterprise_ui_pricing_fuel_quota_unknown')
  const count = new Intl.NumberFormat(numberLocale.value, { notation: 'compact', maximumFractionDigits: 2 }).format(amount)
  return t('enterprise_ui_pricing_characters', { count })
}

function formatUnitPrice(item: FuelBagConfigItem) {
  const amount = Number(item.charNum || 0)
  const price = Number(userStore.bizTeamInfo?.agentUid ? item.bizAgentRealPrice : item.realPrice)
  if (!amount || !Number.isFinite(price)) return ''
  const currency = props.coin?.toUpperCase()
  const symbol = userStore.getCurrentCoinSymbol(props.coin || '')
  const unitChars = currency === 'USD' ? 1_000_000 : 10_000
  const count = new Intl.NumberFormat(numberLocale.value, { notation: 'compact', maximumFractionDigits: 2 }).format(unitChars)
  return t('enterprise_ui_pricing_unit_price', { price: `${symbol}${formatNumber(price / (amount / unitChars))}`, count })
}

function formatPrice(item: FuelBagConfigItem) {
  const value = userStore.bizTeamInfo?.agentUid ? item.bizAgentRealPrice : item.realPrice
  const symbol = userStore.getCurrentCoinSymbol(props.coin || '')
  return `${symbol}${formatNumber(Number(value || 0))}`
}

function formatDiscount(value?: string) {
  const discount = String(value || '').trim()
  if (!discount) return ''
  return t('enterprise_ui_pricing_discount', { discount: discount.replace(/%+$/, '') })
}

function formatNumber(value: number, useGrouping = true) {
  return new Intl.NumberFormat(numberLocale.value, { maximumFractionDigits: 2, useGrouping }).format(value)
}

watch(
  () => [props.coin, props.pricingMode, proStore.proFuelBagConfig, proStore.globalProFuelBagConfig],
  getProList,
  { immediate: true, deep: true },
)

defineExpose({ setDefaultItem, getPayConfigItem, resetSelection })
</script>

<style lang="scss" scoped>
.purchase-fuel-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.purchase-fuel-empty {
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8b95a1;
  font-size: 12px;
  border: 1px dashed #dfe4ea;
  border-radius: 10px;
  background: #fafbfc;
}

.purchase-fuel-option {
  width: 100%;
  min-height: 45px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  color: #1f2329;
  text-align: left;
  border: 1px solid #e3e7ec;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease;

  &:hover,
  &.is-selected {
    border-color: #2f80ed;
    background: #f5f9ff;
  }

  &.is-selected {
    box-shadow: inset 0 0 0 0.5px #2f80ed;
  }

  &__radio {
    width: 15px;
    height: 15px;
    flex: 0 0 15px;
    color: #c3c9d1;
  }

  &.is-selected &__radio {
    color: #2f80ed;
  }

  &__body,
  &__info,
  &__price {
    display: flex;
    align-items: center;
  }

  &__body {
    min-width: 0;
    flex: 1;
    justify-content: space-between;
    gap: 12px;
  }

  &__info {
    min-width: 0;
    gap: 8px;

    strong {
      flex: 0 0 auto;
      color: inherit;
      font-size: 13.5px;
      font-weight: 700;
      line-height: 20px;
    }

    small {
      overflow: hidden;
      color: #8b95a1;
      font-size: 11.5px;
      line-height: 17px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__price {
    flex: 0 0 auto;
    gap: 8px;

    em {
      padding: 2px 6px;
      color: #fff;
      font-size: 9.5px;
      font-style: normal;
      font-weight: 700;
      line-height: 14px;
      border-radius: 4px;
      background: #f04455;
    }

    b {
      color: #2f80ed;
      font-size: 14.5px;
      font-weight: 800;
      line-height: 18px;
      white-space: nowrap;
    }
  }
}

@media (max-width: 640px) {
  .purchase-fuel-option {
    &__body {
      align-items: flex-start;
    }

    &__info,
    &__price {
      flex-wrap: wrap;
    }
  }
}
</style>
